import { memo } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  EventAvailable as EventAvailableIcon,
  CalendarToday as CalendarTodayIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { SCHEDULE_STATUS } from '../constants/statusConfig';
import { pluralize } from '../utils/pluralize';

const formatDateShort = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Retorna a data máxima de abertura: hoje + daysAhead dias. */
const formatOpenUntil = (daysAhead) => {
  const d = new Date();
  d.setDate(d.getDate() + (daysAhead || 30));
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Card de schedule
 */
const ScheduleCard = memo(function ScheduleCard({
  schedule,
  professionalName,
  placeName,
  shifts = [],
  canWrite = true,
  onEdit,
  onDelete,
  onShare,
  onAddShift,
  onEditShift,
  onAppointment,
  formatShiftDays,
  formatTime,
}) {
  const rawStatus = schedule.status || 'Active';
  const statusKey = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
  const statusMeta = SCHEDULE_STATUS[statusKey] || SCHEDULE_STATUS.Active;
  const isDeleted = statusKey === 'Deleted';

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: statusMeta.opacity,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: statusMeta.borderColor, // já é valor fixo em STATUS_META
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h2" noWrap sx={{ flex: 1, pr: 1 }}>
            {schedule.name}
          </Typography>
          {onAppointment && shifts.length > 0 && statusKey === 'Active' && (
            <Button
              variant="outlined"
              size="small"
              color="success"
              startIcon={<EventAvailableIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onAppointment(schedule);
              }}
              sx={{ 
                fontSize: '0.7rem', 
                py: 0.25, 
                px: 1,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              Agendar
            </Button>
          )}
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="primary" fontWeight="medium">
            👨‍⚕️ {professionalName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            📍 {placeName}
          </Typography>
        </Box>
        
        {schedule.description && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {schedule.description}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {/* Status — cor fixa, filled, independente de tema */}
          <Tooltip title={isDeleted || !canWrite ? statusMeta.label : 'Clique para editar a agenda'}>
            <Chip
              label={statusMeta.label}
              size="small"
              onClick={isDeleted || !canWrite ? undefined : (e) => { e.stopPropagation(); onEdit(schedule); }}
              sx={{ cursor: isDeleted || !canWrite ? 'default' : 'pointer', ...statusMeta.chipSx }}
            />
          </Tooltip>

          {/* Turma — exibido quando maxAttendees > 1 */}
          {(schedule.maxAttendees > 1) && (
            <Tooltip title={`Turma: até ${schedule.maxAttendees} participantes por horário`}>
              <Chip
                label={`👥 Turma · ${schedule.maxAttendees} vagas`}
                size="small"
                onClick={isDeleted || !canWrite ? undefined : (e) => { e.stopPropagation(); onEdit(schedule); }}
                sx={{ cursor: isDeleted || !canWrite ? 'default' : 'pointer', bgcolor: '#1b5e20', color: '#FFFFFF', fontWeight: 500 }}
              />
            </Tooltip>
          )}

          {/* Janela de oferecimento de reservas — filled azul */}
          <Tooltip title="Até quantos dias à frente o cliente pode reservar">
            <Chip
              icon={<EventAvailableIcon sx={{ color: '#FFFFFF !important' }} />}
              label={`Aceita reservas até ${formatOpenUntil(schedule.maxBookingDaysAhead)}`}
              size="small"
              onClick={isDeleted || !canWrite ? undefined : (e) => { e.stopPropagation(); onEdit(schedule); }}
              sx={{ cursor: isDeleted || !canWrite ? 'default' : 'pointer', bgcolor: '#1565c0', color: '#FFFFFF', fontWeight: 500 }}
            />
          </Tooltip>

          {/* Mostra X dias — filled roxo */}
          <Tooltip title={isDeleted || !canWrite ? '' : 'Clique para editar a agenda'}>
            <Chip
              label={`Mostra ${schedule.maxAvailableDaysToShow || 7} dias`}
              size="small"
              onClick={isDeleted || !canWrite ? undefined : (e) => { e.stopPropagation(); onEdit(schedule); }}
              sx={{ cursor: isDeleted || !canWrite ? 'default' : 'pointer', bgcolor: '#6a1b9a', color: '#FFFFFF', fontWeight: 500 }}
            />
          </Tooltip>

          {/* Data início — filled teal */}
          {schedule.startDate && (
            <Tooltip title={isDeleted || !canWrite ? '' : 'Clique para editar a agenda'}>
              <Chip
                icon={<CalendarTodayIcon sx={{ color: '#FFFFFF !important' }} />}
                label={`Início: ${formatDateShort(schedule.startDate)}`}
                size="small"
                onClick={isDeleted || !canWrite ? undefined : (e) => { e.stopPropagation(); onEdit(schedule); }}
                sx={{ cursor: isDeleted || !canWrite ? 'default' : 'pointer', bgcolor: '#00695c', color: '#FFFFFF', fontWeight: 500 }}
              />
            </Tooltip>
          )}

          {/* Último dia com horários — filled âmbar se tem data, cinza se sem data definida.
              NÃO é encerramento da agenda (feature 103): a agenda continua ativa e visível
              depois desta data, só para de oferecer horários além dela. */}
          <Tooltip title="Último dia com horários oferecidos para agendamento">
            <Chip
              icon={<EventBusyIcon sx={{ color: '#FFFFFF !important' }} />}
              label={schedule.endDate ? `Horários até ${formatDateShort(schedule.endDate)}` : 'Sem data máxima'}
              size="small"
              onClick={isDeleted || !canWrite ? undefined : (e) => { e.stopPropagation(); onEdit(schedule); }}
              sx={schedule.endDate
                ? { cursor: isDeleted || !canWrite ? 'default' : 'pointer', bgcolor: '#c77000', color: '#FFFFFF', fontWeight: 500 }
                : { cursor: isDeleted || !canWrite ? 'default' : 'pointer', bgcolor: '#9e9e9e', color: '#FFFFFF', fontWeight: 500 }
              }
            />
          </Tooltip>
        </Box>

        {/* Seção de Turnos */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon fontSize="small" />
              Turnos ({shifts.length})
            </Typography>
            {!isDeleted && canWrite && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddShift(schedule);
                }}
                variant="outlined"
              >
                Novo
              </Button>
            )}
          </Box>
          
          {shifts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {shifts.slice(0, 3).map((shift) => {
                const serviceNames = (shift.services || [])
                  .filter(s => s.serviceName)
                  .map(s => s.serviceName);
                const displayServices = serviceNames.length > 5
                  ? serviceNames.slice(0, 5).join(', ') + ', ...'
                  : serviceNames.join(', ');

                return (
                  <Button
                    key={shift.id}
                    variant="outlined"
                    size="small"
                    onClick={isDeleted || !canWrite ? undefined : () => onEditShift(shift)}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      opacity: shift.isActive ? 1 : 0.6,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        <Typography variant="caption" display="block" fontWeight="medium">
                          {formatShiftDays(shift)}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                        </Typography>
                      </Box>
                      {displayServices && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            ml: 'auto',
                            pl: 1,
                            textAlign: 'right',
                            fontSize: '0.65rem',
                            lineHeight: 1.3,
                            maxHeight: '3.9em',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                            minWidth: 0,
                          }}
                        >
                          {displayServices}
                        </Typography>
                      )}
                    </Box>
                  </Button>
                );
              })}
              {shifts.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                  + {shifts.length - 3} {pluralize(shifts.length - 3, 'turno')}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Nenhum turno configurado
            </Typography>
          )}
        </Box>
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ShareIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onShare(schedule);
          }}
          disabled={shifts.length === 0 || statusKey !== 'Active' || !canWrite}
          sx={{ fontWeight: 600 }}
        >
          Compartilhar
        </Button>
        <Box>
          {!isDeleted && (
            <Tooltip title={!canWrite ? 'Sem permissão para editar esta agenda' : 'Editar agenda'}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!canWrite}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(schedule);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {statusKey !== 'Deleted' && (
            <Tooltip title={!canWrite ? 'Sem permissão para excluir esta agenda' : 'Excluir agenda'}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canWrite}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(schedule);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
  );
});

export default ScheduleCard;
